import type { DatePickerDateRange } from '@/core/features/date-picker/date-picker-types';
import type {
    SchoolProductAvailabilityAttributes,
    SchoolProductAvailabilityAttributes_jsonld_read,
} from '@/features/school-product-availability/school-product-availability-type';
import type { ReservationType } from '@/features/school-product-tiles-v2/school-product-tiles-v2-type';

import React from 'react';

import {
    createDateRangeArray,
    dateToApiDate,
    getNumberOfMonthsBetween,
    getPastDaysInCurrentMonth,
    isSameDate,
} from '@/core/features/date/date';
import { useQuery } from '@/core/features/react-query/react-query-service';
import { DEFAULT_QUERY_STALE_TIME } from '@/core/features/request/request-constants';
import { getSchoolAvailabilityClientSide } from '@/features/school-product-availability/school-product-availability-service';

type UseSchoolProductAvailability = {
    productId: string;
    schoolId: number;
    type: ReservationType;
};

export function useSchoolProductAvailability({ productId, schoolId, type }: UseSchoolProductAvailability) {
    const isInitialized = React.useRef(false);
    const [selectedDateRange, setSelectedDateRange] = React.useState<DatePickerDateRange>();
    const [schoolProductAvailability, setSchoolProductAvailability] =
        React.useState<SchoolProductAvailabilityAttributes_jsonld_read>();
    const [queryKey, setQueryKey] = React.useState<string[]>([]);

    const { data, isLoading } = useQuery(
        'useSchoolProductAvailability',
        ['school-product-availability', schoolId, productId, ...queryKey],
        () => getSchoolAvailabilityClientSide({ productId, schoolId, selectedDateRange, type }),
        {
            staleTime: DEFAULT_QUERY_STALE_TIME,
        },
    );

    const disabledDays: Date[] = React.useMemo(() => {
        if (!schoolProductAvailability) {
            return [];
        }

        const convertedDisabledDays = schoolProductAvailability.disabledDays.map(
            (disabledDay) => new Date(disabledDay),
        );
        const pastDaysInCurrentMonth = getPastDaysInCurrentMonth(
            new Date(schoolProductAvailability.earliestBookableDate),
        );
        return [...pastDaysInCurrentMonth, ...convertedDisabledDays];
    }, [schoolProductAvailability]);

    const datePickerRange: Date[] = React.useMemo(() => {
        if (!schoolProductAvailability) {
            return [];
        }

        const earliestBookableDate = new Date(schoolProductAvailability.earliestBookableDate);
        const bookableMonthsFromEarliestBookableDate =
            schoolProductAvailability.bookableMonthsFromNow -
            getNumberOfMonthsBetween(new Date(), earliestBookableDate);

        return createDateRangeArray(bookableMonthsFromEarliestBookableDate, earliestBookableDate);
    }, [schoolProductAvailability]);

    const countLimits: SchoolProductAvailabilityAttributes['count'] = React.useMemo(() => {
        if (!schoolProductAvailability?.count) {
            return { max: 0, min: 0 };
        }

        return {
            max: Math.max(schoolProductAvailability.count.min, schoolProductAvailability.count.max),
            min: schoolProductAvailability.count.min,
        };
    }, [schoolProductAvailability?.count]);

    const earliestBookableDate: Date = React.useMemo(() => {
        if (!schoolProductAvailability) {
            return new Date();
        }

        return new Date(schoolProductAvailability.earliestBookableDate);
    }, [schoolProductAvailability]);

    const getLastBookableDate = React.useCallback(() => {
        if (!schoolProductAvailability) {
            return new Date();
        }

        const earliestBookableDate = new Date(schoolProductAvailability.earliestBookableDate);
        const lastDayOfMonth = new Date(
            earliestBookableDate.getUTCFullYear(),
            earliestBookableDate.getUTCMonth() + schoolProductAvailability.bookableMonthsFromNow,
            0,
        );

        return lastDayOfMonth;
    }, [schoolProductAvailability]);

    // data update
    React.useEffect(() => {
        if (!data) {
            return;
        }

        isInitialized.current = true;
        const newData = data;
        const { endDate, startDate } = newData;
        setSchoolProductAvailability(newData);

        if (startDate) {
            setSelectedDateRange((value) => ({
                from: new Date(startDate),
                to: value?.to,
            }));
        }

        if (endDate) {
            setSelectedDateRange((value) => ({
                from: value?.from,
                to: new Date(endDate),
            }));
        }
    }, [data]);

    const updateSelectedStartDate = (date: Date) => {
        setSelectedDateRange((preValue) => {
            if (preValue?.from && isSameDate(date, preValue.from)) {
                return preValue;
            }

            return {
                from: date,
                to: undefined,
            };
        });
        setQueryKey([dateToApiDate(date)]);
    };

    return {
        cancellation: schoolProductAvailability?.cancellation ?? null,
        countLimits,
        datePickerRange,
        disabledDays,
        earliestBookableDate,
        getLastBookableDate,
        isLoading,
        offerId: schoolProductAvailability?.offerId,
        selectedDateRange,
        unitPrice: schoolProductAvailability?.unitPrice ?? 0,
        updateSelectedStartDate,
    };
}
